import React, { useState } from 'react';
import { motion } from 'framer-motion';
import plan1Icon from '../../assets/icons/plan1.png';
import plan2Icon from '../../assets/icons/plan2.png';
import plan3Icon from '../../assets/icons/plan3.png';
import arrowIcon from '../../assets/icons/arrow.png';
import tickIcon from '../../assets/icons/tick.png';

function Pricing() {
  const [isWeekly, setIsWeekly] = useState(true);

  const togglePricing = () => {
    setIsWeekly(!isWeekly);
  };

  const planIcons = [plan1Icon, plan2Icon, plan3Icon];

  const plans = [
    {
      name: 'Basic Plan',
      icon: '⚡',
      price: isWeekly ? '$29' : '$99',
      period: isWeekly ? '/Weekly' : '/Monthly',
      description: 'Perfect match for small real estate agencies or individual agents with limited budgets.',
      buttonText: 'Try For Free',
      benefits: [
        { title: 'Easy to use interface', content: 'Intuitive design for quick setup and navigation.' },
        { title: 'Affordable pricing', content: 'Budget-friendly option for those starting out.' },
        { title: 'Solid foundation', content: 'A great starting point for understanding lead generation tools.' },
      ],
    },
    {
      name: 'Standard Plan',
      icon: '🏆',
      price: isWeekly ? '$49' : '$169',
      period: isWeekly ? '/Weekly' : '/Monthly',
      description: 'Perfect match for Mid-sized real estate agencies or agents seeking more comprehensive features.',
      buttonText: 'Get Started Now',
      benefits: [
        { title: 'Includes all features from the Basic plan, plus', content: 'Advanced analytics, CRM integration, and expanded email template options.' },
        { title: 'In-depth analytics', content: 'Track performance and identify areas for improvement.' },
        { title: 'Streamlined workflow', content: 'Integrate with your CRM for efficient lead management.' },
        { title: 'Customization options', content: 'Tailor your templates and settings to fit your specific needs.' },
      ],
    },
    {
      name: 'Enterprise Plan',
      icon: '🚀',
      price: 'Custom',
      period: '',
      description: 'Perfect match for Large real estate agencies or teams with high-volume lead generation.',
      buttonText: 'Contact us',
      benefits: [
        { title: 'Includes all features from the Standard plan, plus', content: 'Custom branding options, priority customer support, and additional integrations.' },
        { title: 'Tailored solutions', content: 'Get a customized plan that meets your unique needs.' },
        { title: 'Dedicated support', content: 'Enjoy priority assistance from our expert team.' },
        { title: 'Scalability', content: 'Easily adapt to your growing business and changing requirements.' },
      ],
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/5 to-[#543DF4]/5" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#C334F6]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#543DF4]/10 rounded-full blur-3xl animate-float-reverse" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#C334F6]/5 to-[#543DF4]/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="pt-40 pb-20 px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <span className="inline-block px-6 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-white">
              Pricing Plans
            </span>
          </motion.div>

          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-5xl font-normal leading-[1.2] tracking-[0.001em] text-center mb-12 text-white max-w-[800px] mx-auto"
          >
            Flexible Plans to
            <br />
            <span className="bg-gradient-to-r from-[#C334F6] to-[#543DF4] bg-clip-text text-transparent">
              Fit Your Budget
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex justify-center mb-12"
          >
            <div className="relative inline-flex items-center p-1 rounded-full bg-white/5 backdrop-blur-sm border border-white/10">
              <motion.button
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  isWeekly 
                    ? 'bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white' 
                    : 'text-white/60 hover:text-white'
                }`}
                onClick={togglePricing}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Weekly
              </motion.button>
              <motion.button
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  !isWeekly 
                    ? 'bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white' 
                    : 'text-white/60 hover:text-white'
                }`}
                onClick={togglePricing}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Monthly
              </motion.button>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className={`relative p-8 rounded-2xl backdrop-blur-sm ${
                  index === 1 
                    ? 'bg-gradient-to-br from-[#C334F6]/20 to-[#543DF4]/20 border-2 border-[#C334F6]/30' 
                    : 'bg-white/5 border border-white/10'
                }`}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/20 to-[#543DF4]/20 rounded-xl blur-md" />
                    <div className="relative p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                      <img 
                        src={planIcons[index]} 
                        alt={`${plan.name} icon`} 
                        className="w-8 h-8"
                      />
                    </div>
                  </div>
                  <h2 className="text-xl font-medium text-white ml-4">
                    {plan.name}
                  </h2>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">
                    {plan.price}
                  </span>
                  <span className="text-white/60 ml-2">
                    {plan.period}
                  </span>
                </div>

                <div className="flex items-start mb-8">
                  <div className="relative p-2 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 mr-3">
                    <img 
                      src={arrowIcon} 
                      alt="Arrow icon" 
                      className="w-4 h-4"
                    />
                  </div>
                  <p className="text-white/80 text-sm">
                    {plan.description}
                  </p>
                </div>

                <motion.button
                  className={`w-full py-4 rounded-full font-medium mb-8 ${
                    index === 1 
                      ? 'bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white shadow-lg shadow-[#C334F6]/20' 
                      : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {plan.buttonText}
                </motion.button>

                <h3 className="text-lg font-medium text-white mb-4">
                  Benefits:
                </h3>

                <ul className="space-y-4">
                  {plan.benefits.map((benefit, benefitIndex) => (
                    <motion.li
                      key={benefitIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: benefitIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <div className="relative p-1.5 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 mr-3">
                        <img 
                          src={tickIcon} 
                          alt="Tick icon" 
                          className="w-4 h-4"
                        />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">
                          {benefit.title}
                        </h4>
                        <p className="text-white/60 text-sm">
                          {benefit.content}
                        </p>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Pricing;